import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Chip, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningPage from './WarningPage';
import useWarningMonth from './useWarningMonth';
import RetryNotice from './RetryNotice';
import { departmentLabel } from './departments';
import WarningLoading from './WarningLoading';
import FlagSummary from './FlagSummary';
import apiRequest from '../../api/request';
import WarningMetricTable from './WarningMetricTable';
import useConfigAccess from './useConfigAccess';

const DEPARTMENTS = [
  { id: 11, name: 'OGD' },
  { id: 12, name: 'CSD' },
  { id: 3, name: 'Foundation' },
  { id: 20, name: 'Classroom' },
  { id: 21, name: 'Little Genie' },
  { id: 22, name: 'Speak Pro' },
  { id: 35, name: 'SkillX' },
];
const departmentName = (id) => departmentLabel(id, DEPARTMENTS);

const StatusCell = ({ isWarning }) =>
  isWarning ? (
    <WarningAmberIcon fontSize="small" sx={{ color: 'error.main', display: 'block', mx: 'auto' }} />
  ) : (
    <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main', display: 'block', mx: 'auto' }} />
  );

const ThresholdCell = ({ threshold, slabStatus }) => {
  if (slabStatus === 'no_slabs_for_department') {
    return <Chip size="small" color="warning" variant="outlined" label="No slab for this month" title="No slab is in force for this department in the selected month. Add one in Slab Configuration and set 'Applies from' to this month or earlier." />;
  }
  if (slabStatus === 'gap') {
    return <Chip size="small" color="warning" variant="outlined" label="Target is in no slab" title="This person's target falls outside every slab set for this department in the selected month - add or widen a slab." />;
  }
  if (slabStatus === 'no_target') {
    return <Typography variant="body2" color="text.secondary" title="This person has no revenue target for the month, so no slab (and no threshold) applies - they are shown with their count but never flagged.">No target set</Typography>;
  }
  return threshold === null
    ? <Typography variant="body2" color="text.secondary">Not configured</Typography>
    : `< ${threshold}`;
};

// Number of Admissions results for the selected month - a raw count of students, for everyone who made admissions. The threshold
// is the applicable slab's admission-count threshold, so only people with a Target Revenue have one; the rest read "No target set"
// and are never flagged (see admission_count_dashboard.controller.js).
const AdmissionCountDashboard = () => {
  // The month is shared across the whole dashboard and remembered for the session (useWarningMonth.js).
  const [month, setMonth] = useWarningMonth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ totalEvaluated: 0, warningCount: 0, needsSlabConfigCount: 0, noTargetCount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0); // Retry re-runs just this request

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiRequest({ url: `warning-dashboard/admission-count-dashboard?month=${month.format('YYYY-MM-DD')}`, method: 'get' })
      .then((res) => {
        if (cancelled) return;
        // apiRequest resolves even on a failed call (src/api/request.js) - without this check a server error
        // rendered as an empty page (or zeros) instead of a message
        if (res?.status !== 'success') {
          setError(res?.message || 'Could not load Admission Count results');
          return;
        }
        setRows(res?.data?.rows || []);
        setSummary({
          totalEvaluated: res?.data?.totalEvaluated || 0,
          warningCount: res?.data?.warningCount || 0,
          needsSlabConfigCount: res?.data?.needsSlabConfigCount || 0,
          noTargetCount: res?.data?.noTargetCount || 0,
        });
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || 'Could not load Admission Count results');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [month, reloadKey]);

  const dangerScore = (row) => (row.threshold === null ? -Infinity : row.threshold - row.value);

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => dangerScore(b) - dangerScore(a)),
    [rows]
  );

  const thresholdCsv = (row) => {
    if (row.slabStatus === 'no_slabs_for_department') return 'No slab for this month';
    if (row.slabStatus === 'gap') return 'Target is in no slab';
    if (row.slabStatus === 'no_target') return 'No target set';
    return row.threshold === null ? 'Not configured' : `< ${row.threshold}`;
  };

  const columns = [
    { field: 'status', headerName: 'Status', align: 'center', render: (row) => <StatusCell isWarning={row.isWarning} />, csvValue: (row) => (row.isWarning ? 'Warning' : 'OK') },
    {
      field: 'entityName',
      headerName: 'Name',
      render: (row) => (
        <Typography
          variant="body2"
          fontStyle={row.isActive === false ? 'italic' : 'normal'}
          color={row.isActive === false || row.isWarning ? 'error.main' : 'text.primary'}
        >
          {row.entityName}
          {row.isActive === false && ' (Inactive)'}
        </Typography>
      ),
    },
    { field: 'departmentId', headerName: 'Department', render: (row) => departmentName(row.departmentId), csvValue: (row) => departmentName(row.departmentId) },
    {
      field: 'admissionsCount',
      headerName: 'Admissions',
      align: 'right',
      render: (row) => (
        <Typography variant="body2" fontWeight={row.isWarning ? 700 : 400} color={row.isWarning ? 'error.main' : 'text.primary'}>
          {row.admissionsCount}
        </Typography>
      ),
    },
    {
      field: 'threshold',
      headerName: 'Threshold',
      align: 'right',
      render: (row) => <ThresholdCell threshold={row.threshold} slabStatus={row.slabStatus} />,
      csvValue: thresholdCsv,
    },
  ];

  const { canConfigure } = useConfigAccess();

  return (
    <WarningPage
      title="Number of Admissions"
      actions={
        <Box display="flex" gap={1}>
          <Button component={Link} to={`/dashboard/warning-dashboard?month=${month.format('YYYY-MM-DD')}`} variant="text">
            Back to Warning Dashboard
          </Button>
          {canConfigure('target-slab-config') && (
            <Button component={Link} to={`/dashboard/warning-dashboard/target-slab-config?month=${month.format('YYYY-MM-DD')}`} variant="outlined">
              Configure Slabs
            </Button>
          )}
        </Box>
      }
    >
      <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker views={['year', 'month']} label="Month" value={month} onChange={(v) => v && setMonth(v)} />
        </LocalizationProvider>
      </Box>

      {loading && <WarningLoading />}

      {!loading && error && <RetryNotice message={error} onRetry={() => setReloadKey((k) => k + 1)} />}

      {!loading && !error && (
        <FlagSummary warningCount={summary.warningCount} totalEvaluated={summary.totalEvaluated}>
            {summary.needsSlabConfigCount > 0 && ` · ${summary.needsSlabConfigCount} have no matching slab`}
            {summary.noTargetCount > 0 && ` · ${summary.noTargetCount} have no target, so no threshold`}
          </FlagSummary>
      )}

      {!loading && !error && (
        <WarningMetricTable
          rows={sortedRows}
          columns={columns}
          departmentName={departmentName}
          emptyMessage="No admissions were made this period."
          exportFileName="Admission_Count"
        />
      )}
    </WarningPage>
  );
};

export default AdmissionCountDashboard;
